<template>
  <section
    class="relative mx-auto mt-6 w-full max-w-lg overflow-hidden rounded-lg border border-gray-200 bg-white"
    data-testid="completion"
  >
    <div class="pointer-events-none absolute inset-x-0 top-0 h-44 bg-linear-to-b from-green-50 to-transparent" />
    <div class="relative flex flex-col items-center px-5 py-8 text-center md:py-10">
      <CheckBurst
        class="mb-4 size-16"
        :rings="[{ color: 'border-green-400', delay: '140ms' }, { color: 'border-green-300', delay: '320ms' }]"
        stamp-class="bg-green-100 ring-4 ring-white"
        stamp-delay="80ms"
      >
        <svg
          viewBox="0 0 32 32"
          class="size-8"
          fill="none"
        >
          <path
            class="check-tick stroke-green-500"
            style="--d: 320ms"
            d="M9.5 16.5 L14 21 L22.5 11"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </CheckBurst>
      <h2
        class="completion-up text-xl font-semibold text-gray-900 md:text-2xl"
        style="--d: 380ms"
      >
        {{ t('title') }}
      </h2>
      <p
        class="completion-up mt-2 max-w-sm text-md text-gray-500"
        style="--d: 460ms"
      >
        {{ t('description') }}
      </p>
      <div
        class="completion-up mt-6 flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center"
        style="--d: 540ms"
      >
        <ZoneButton
          variant="outline"
          color="gray_alt"
          class="hover:bg-gray-100!"
          data-testid="completion-review"
          @click="$emit('review')"
        >
          {{ t('review') }}
        </ZoneButton>
        <ZoneButton
          color="primary_alt"
          :href="dashboardUrl"
          trailing-icon="arrow_forward"
          data-testid="completion-dashboard"
        >
          {{ t('go_to_dashboard') }}
        </ZoneButton>
      </div>
    </div>
    <div
      v-if="!sidebarDismissed"
      class="relative flex flex-col gap-2 border-t border-gray-200 bg-gray-50 px-5 py-3 sm:flex-row sm:items-center md:px-6"
      data-testid="completion-dismiss-footer"
    >
      <i18n-t
        keypath="dismiss_rule"
        tag="p"
        class="min-w-0 flex-1 text-xs text-gray-500"
      >
        <template #icon>
          <ZoneIcon
            icon="schedule"
            class="align-[-0.3rem] text-sm! text-gray-400"
            aria-hidden="true"
          />
        </template>
        <template #menu_item><span class="font-medium text-gray-700">{{ t('menu_item') }}</span></template>
        <template #date><span class="font-medium text-gray-700">{{ autoDismissOn }}</span></template>
        <template #days>{{ autoDismissDays }}</template>
        <template #organization>{{ organizationName }}</template>
      </i18n-t>
      <p
        v-if="dismissFailed"
        class="text-xs text-red-700"
        role="alert"
      >
        {{ t('unexpected_error') }}
      </p>
      <button
        type="button"
        class="inline-flex shrink-0 items-center self-start rounded-md px-2 py-1 text-sm font-medium text-gray-500 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-primary-700 disabled:opacity-50 sm:self-auto"
        :disabled="dismissing"
        data-testid="completion-dismiss"
        @click="dismiss"
      >
        {{ t('dismiss') }}
      </button>
    </div>
  </section>
</template>

<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ZoneButton from 'zone/components/base/Button.vue';
import ZoneIcon from 'zone/components/base/Icon.vue';
import CheckBurst from '~/components/experiments/0239_getting_started_checklist/CheckBurst.vue';
import { UserOrganizationsResource } from '~/resources';

const { dashboardUrl, userOrganizationId, organizationName, autoDismissOn, autoDismissDays, sidebarDismissed } = defineProps({
  dashboardUrl: { type: String, required: true },
  userOrganizationId: { type: Number, required: true },
  organizationName: { type: String, required: true },
  autoDismissOn: { type: String, required: true },
  autoDismissDays: { type: Number, required: true },
  sidebarDismissed: { type: Boolean },
});

defineEmits(['review']);

defineOptions({ name: 'ChecklistCompletion' });

const { t } = useI18n();

const dismissing = ref(false);
const dismissFailed = ref(false);

const dismiss = async () => {
  dismissing.value = true;
  dismissFailed.value = false;

  try {
    const response = await UserOrganizationsResource.dismissDialog(
      { id: userOrganizationId },
      { dialog: 'ar0239_getting_started_sidebar' },
      { abortReload: true },
    );

    if (response?.success) {
      window.location.reload();
    } else {
      dismissFailed.value = true;
    }
  } catch {
    dismissFailed.value = true;
  } finally {
    dismissing.value = false;
  }
};
</script>

<style scoped>
@keyframes completion-up { from { opacity: 0; transform: translateY(0.8rem); } to { opacity: 1; transform: none; } }

.completion-up { animation: completion-up 320ms cubic-bezier(0.16, 1, 0.3, 1) var(--d, 0ms) both; }

@media (prefers-reduced-motion: reduce) {
  .completion-up { animation: none; }
}
</style>

<i18n lang="json" src="~/components/experiments/0239_getting_started_checklist/steps.json"></i18n>

<i18n lang="json">
{
  "en": {
    "title": "Setup complete",
    "description": "Your team can start tracking, and everything you scheduled will arrive on its own.",
    "review": "Review what you set up",
    "dismiss": "Dismiss",
    "dismiss_rule": "{icon} Dismiss removes {menu_item} from your menu. It also goes on its own on {date} — {days} days after {organization} was created. This page stays reachable either way.",
    "menu_item": "Get started"
  }
}
</i18n>
